// features/findings/charts/country-scatter.js
// XY scatter — used for cross-country / cross-domain comparison.
// Points labeled; outliers can be visually emphasized.

export function CountryScatter({ title, points, xLabel, yLabel, xMin, xMax, yMin, yMax }) {
  if (!points || points.length < 2) return null;

  const xs = points.map((p) => p.x);
  const ys = points.map((p) => p.y);
  const minX = xMin ?? Math.min(...xs);
  const maxX = xMax ?? Math.max(...xs);
  const minY = yMin ?? Math.min(...ys);
  const maxY = yMax ?? Math.max(...ys);

  const W = 720;
  const H = 320;
  const ML = 64;
  const MR = 32;
  const MT = 24;
  const MB = 56;
  const innerW = W - ML - MR;
  const innerH = H - MT - MB;
  const xScale = (x) => ML + ((x - minX) / (maxX - minX || 1)) * innerW;
  const yScale = (y) => MT + ((maxY - y) / (maxY - minY || 1)) * innerH;

  const yTicks = 5;
  const yTickVals = Array.from({ length: yTicks }, (_, i) =>
    Number((minY + ((maxY - minY) * i) / (yTicks - 1)).toFixed(2))
  );
  const xTicks = 5;
  const xTickVals = Array.from({ length: xTicks }, (_, i) =>
    Number((minX + ((maxX - minX) * i) / (xTicks - 1)).toFixed(2))
  );

  return (
    <figure className="finding-chart">
      <figcaption className="finding-chart-caption">{title}</figcaption>
      <div className="finding-svg-wrap">
        <svg viewBox={`0 0 ${W} ${H}`} className="finding-svg" role="img" aria-label={title}>
          {/* Y grid + labels */}
          {yTickVals.map((t) => (
            <g key={t}>
              <line x1={ML} y1={yScale(t)} x2={W - MR} y2={yScale(t)} className="finding-svg-grid" />
              <text x={ML - 8} y={yScale(t)} className="finding-svg-axis-label" textAnchor="end" dominantBaseline="middle">
                {t}
              </text>
            </g>
          ))}
          {/* X axis + ticks */}
          <line x1={ML} y1={H - MB} x2={W - MR} y2={H - MB} className="finding-svg-axis" />
          {xTickVals.map((t) => (
            <g key={t}>
              <line x1={xScale(t)} y1={H - MB} x2={xScale(t)} y2={H - MB + 4} className="finding-svg-axis" />
              <text x={xScale(t)} y={H - MB + 18} className="finding-svg-axis-label" textAnchor="middle">
                {t}
              </text>
            </g>
          ))}
          {/* Zero line for Y if range crosses 0 */}
          {minY < 0 && maxY > 0 && (
            <line
              x1={ML} y1={yScale(0)} x2={W - MR} y2={yScale(0)}
              className="finding-svg-zero"
            />
          )}
          {/* Axis labels */}
          <text x={ML + innerW / 2} y={H - 8} className="finding-svg-axis-title" textAnchor="middle">
            {xLabel}
          </text>
          <text x={16} y={MT + innerH / 2} className="finding-svg-axis-title" textAnchor="middle"
                transform={`rotate(-90 16 ${MT + innerH / 2})`}>
            {yLabel}
          </text>

          {/* Points */}
          {points.map((p) => {
            const cx = xScale(p.x);
            const cy = yScale(p.y);
            const tone = p.tone || 'neutral';
            const r = p.outlier ? 6 : 4.5;
            return (
              <g key={p.label}>
                <circle cx={cx} cy={cy} r={r}
                  className={`finding-svg-dot finding-svg-dot-${tone}${p.outlier ? ' finding-svg-dot-outlier' : ''}`} />
                <text
                  x={cx + (p.labelDx ?? 8)}
                  y={cy + (p.labelDy ?? 4)}
                  className={`finding-svg-point-label${p.outlier ? ' finding-svg-point-label-outlier' : ''}`}
                >
                  {p.label}
                </text>
              </g>
            );
          })}
        </svg>
      </div>
    </figure>
  );
}

export default CountryScatter;
